import { GENERATE_AI_IMAGE_PROMPT } from 'apps/web/constants/imageGeneration';
import { MultigradeFormat } from 'apps/web/enums/multigradeFormat';
import { PageKey } from 'apps/web/enums/pageKey';
import { Portal } from 'apps/web/enums/portal.enum';
import {
  TopicExplorerType,
  UnpackType,
} from 'apps/web/enums/topicExplorer.enum';
import {
  dllProperties,
  FormContents,
  pptMakerProperties,
  quizMakerSingleProperties,
  summativeTestProperties,
  unitPlanProperties,
} from 'apps/web/types/page';

import {
  ACTIVITIES_PROMPT_NAME,
  ACTIVITIES_PROMPT_NAME_AMERICAN,
  ACTIVITIES_PROMPT_NAME_DEFAULT,
  AMERICAN_OBJECTIVES_PROMPT_NAME,
  ANALOGYCONTEXTUALIZER_PROMPT_NAME,
  CATCH_UP_FRIDAYS_PROMPT_NAME,
  CEREBRO_LESSON_PLAN_PROMPT_NAME,
  DAILY_LESSON_LOG_G1G12_PROMPT_NAME,
  DAILY_LESSON_LOG_KINDER_PROMPT_NAME,
  DAILY_LESSON_LOG_MATATAG_PROMPT_NAME,
  DLLFormat,
  ELIF_PROMPT_NAME,
  ENERGIZER_PROMPT_NAME,
  IB_SUMMATIVE_TEST_PROMPT_NAME,
  OBJECTIVES_PROMPT_NAME,
  PPT_MAKER_PROMPT_NAME,
  PPT_MAKER_PROMPT_NAME_SUMMARIZED,
  PPTContentStyle,
  QUIZMAKER_AMERICAN_PROMPT_NAME,
  QUIZMAKER_COLLEGE_PROMPT_NAME,
  QUIZMAKERSINGLE_PROMPT_NAME,
  SUBJECT_EXTENSION_PROMPT_NAME,
  SUMMATIVETEST_MATATAG_PROMPT_NAME,
  TOPIC_EXPLORER_PROMPT_NAME,
  TOPIC_EXPLORER_PROMPT_NAME_PREMIUM,
  SUMMATIVETEST_PROMPT_NAME,
  GENERIC_UNPACKING_TOPIC_PROMPT_NAME,
  GENERIC_UNPACKING_LC_PROMPT_NAME,
  USA_SUMMATIVE_TEST_PROMPT_NAME,
  IPLAN_OBJECTIVES_PROMPT_NAME,
  MULTIGRADE_LP_PROMPT_NAME,
  MULTIGRADE_LP_IPLAN_PROMPT_NAME,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

function getObjectiveFormat(portal: Portal) {
  switch (portal) {
    case Portal.USA:
      return AMERICAN_OBJECTIVES_PROMPT_NAME;
    default:
      return OBJECTIVES_PROMPT_NAME;
  }
}

function getDllLessonFormat(
  formContents: dllProperties,
  prefix: string,
  gradeLevel: string,
  dllDays: number,
) {
  if (dllDays > 3) {
    dllDays = 3;
  }

  return () => {
    if (formContents.dllFormat === DLLFormat.MATATAG) {
      return `${prefix}${DAILY_LESSON_LOG_MATATAG_PROMPT_NAME}${dllDays}`;
    } else if (gradeLevel === 'Kindergarten') {
      return `${prefix}${DAILY_LESSON_LOG_KINDER_PROMPT_NAME}`;
    } else {
      return `${prefix}${DAILY_LESSON_LOG_G1G12_PROMPT_NAME}obj${dllDays}`;
    }
  };
}

function getQuizmakerFormat(
  formContents: FormContents,
  isDefaultPortal: boolean,
) {
  if (isDefaultPortal) {
    return 'ShortQuiz';
  }

  const gradeLevel = (formContents as quizMakerSingleProperties).quizGradeLevel;
  const curriculumFramework = (formContents as quizMakerSingleProperties)
    .quizCurriculumFramework;
  if (curriculumFramework) {
    return QUIZMAKER_AMERICAN_PROMPT_NAME;
  }

  const promptName =
    gradeLevel === 'College'
      ? QUIZMAKER_COLLEGE_PROMPT_NAME
      : QUIZMAKERSINGLE_PROMPT_NAME;
  return promptName;
}

function getSummativeTestFormat(
  formContents: FormContents,
  isInternationalPortal: boolean,
  isUSAPortal: boolean,
) {
  if (isInternationalPortal) {
    return IB_SUMMATIVE_TEST_PROMPT_NAME;
  }

  if (isUSAPortal) {
    return USA_SUMMATIVE_TEST_PROMPT_NAME;
  }

  const format = (formContents as summativeTestProperties).format;
  return format === 'MATATAG'
    ? SUMMATIVETEST_MATATAG_PROMPT_NAME
    : SUMMATIVETEST_PROMPT_NAME;
}

function getUnitPlanFormat(formContents: FormContents) {
  const promptName = (formContents as unitPlanProperties).format;
  return promptName;
}

function getMultigradeLpFormat(formContents: FormContents) {
  const lessonFormat = formContents['mgLessonFormat'];
  return lessonFormat === MultigradeFormat.IPLAN
    ? MULTIGRADE_LP_IPLAN_PROMPT_NAME
    : MULTIGRADE_LP_PROMPT_NAME;
}

function getActivitiesFormat(portal: Portal) {
  switch (portal) {
    case Portal.DEFAULT:
      return ACTIVITIES_PROMPT_NAME_DEFAULT;
    case Portal.USA:
      return ACTIVITIES_PROMPT_NAME_AMERICAN;
    default:
      return ACTIVITIES_PROMPT_NAME;
  }
}

function getTopicExplorerFormat(
  formContents: FormContents,
  isPremium: boolean,
) {
  const explorerType = formContents['explorerType'];

  if (explorerType === TopicExplorerType.BREAKDOWN) {
    const itemToUnpack = formContents['itemToUnpack'];
    const subject = formContents['subject'];
    const gradeLevel = formContents['gradeLevel'];

    if (itemToUnpack) {
      formContents['unpackItem'] = itemToUnpack;
      formContents['guItemToUnpack'] = itemToUnpack;
    }

    if (subject) {
      formContents['guSubject'] = subject;
    }

    if (gradeLevel) {
      formContents['guGradeLevel'] = gradeLevel;
    }

    formContents['guUnpackType'] = UnpackType.TOPIC;

    return GENERIC_UNPACKING_TOPIC_PROMPT_NAME;
  }

  const promptName = isPremium
    ? TOPIC_EXPLORER_PROMPT_NAME_PREMIUM
    : TOPIC_EXPLORER_PROMPT_NAME;
  return promptName;
}

function getLessonPlanFormat(formContents) {
  const lessonPlan =
    ['lessonFormat', 'lpLessonFormat', 'mgLessonFormat']
      .map((key) => formContents[key])
      .find((value) => !!value) || '';
  return lessonPlan;
}

function getPrefix(
  pageType: PageKey,
  isFilipinoLanguage: boolean,
  isDefaultPortal: boolean,
) {
  if (isFilipinoLanguage) {
    return 'filipinoFirst';
  }

  if ([PageKey.activities, PageKey.quizMakerSingle].includes(pageType)) {
    return isDefaultPortal ? 'default' : '';
  }

  return '';
}

function getPptMakerPromptName(contentStyle: PPTContentStyle) {
  switch (contentStyle) {
    case PPTContentStyle.SUMMARIZED:
      return PPT_MAKER_PROMPT_NAME_SUMMARIZED;
    case PPTContentStyle.DEFAULT:
      return PPT_MAKER_PROMPT_NAME;
    default:
      return PPT_MAKER_PROMPT_NAME;
  }
}

export default function getLessonFormat(
  formContents: FormContents,
  pageKey: PageKey,
  language: string,
  gradeLevel?: string,
  dllDays?: number,
  portal?: Portal,
  canGeneratePremium?: boolean,
) {
  const isDefaultPortal = portal === Portal.DEFAULT;
  const isInternationalPortal = portal === Portal.INTERNATIONAL;
  const isFilipinoLanguage = language === Language.FILIPINO;
  const isUSAPortal = portal === Portal.USA;

  const prefix = getPrefix(pageKey, isFilipinoLanguage, isDefaultPortal);

  const lessonFormatMap = {
    [PageKey.lessonPlan]: () => prefix + getLessonPlanFormat(formContents),
    [PageKey.objective]: () => prefix + getObjectiveFormat(portal),
    [PageKey.quizMakerSingle]: () =>
      prefix + getQuizmakerFormat(formContents, isDefaultPortal),
    [PageKey.activities]: () => prefix + getActivitiesFormat(portal),
    [PageKey.subjectExtention]: () => prefix + SUBJECT_EXTENSION_PROMPT_NAME,
    [PageKey.analogyContextualizer]: () =>
      prefix + ANALOGYCONTEXTUALIZER_PROMPT_NAME,
    [PageKey.dailyLessonLog]: getDllLessonFormat(
      formContents as dllProperties,
      prefix,
      gradeLevel,
      dllDays,
    ),
    [PageKey.catchUpFridays]: () => prefix + CATCH_UP_FRIDAYS_PROMPT_NAME,
    [PageKey.summativeTest]: () =>
      prefix +
      getSummativeTestFormat(formContents, isInternationalPortal, isUSAPortal),
    [PageKey.cerebroLessonPlan]: () => prefix + CEREBRO_LESSON_PLAN_PROMPT_NAME,
    [PageKey.topicExplorer]: () => {
      return (
        prefix +
        getTopicExplorerFormat(formContents, canGeneratePremium || false)
      );
    },
    [PageKey.energizer]: () => prefix + ENERGIZER_PROMPT_NAME,
    [PageKey.elif]: () => prefix + ELIF_PROMPT_NAME,
    [PageKey.unitPlan]: () => prefix + getUnitPlanFormat(formContents),
    [PageKey.pptMaker]: () =>
      prefix +
      getPptMakerPromptName((formContents as pptMakerProperties).contentStyle),
    [PageKey.photoBooth]: () => prefix + GENERATE_AI_IMAGE_PROMPT,
    [PageKey.genericUnpacking]: () => prefix + GENERIC_UNPACKING_LC_PROMPT_NAME,
    [PageKey.iplanObjective]: () => prefix + IPLAN_OBJECTIVES_PROMPT_NAME,
    [PageKey.multigradeLessonPlan]: () =>
      prefix + getMultigradeLpFormat(formContents),
  };

  const getLessonFormatFunc = lessonFormatMap[pageKey];
  return getLessonFormatFunc ? getLessonFormatFunc() : '';
}
